// Composer-side draft binding.
//
// The hook hydrates the composer's text from IndexedDB on mount,
// schedules debounced saves on every change, and clears the row on
// successful send / unmount-with-flush. The composer (`MessageComposer`
// or the threaded inline composer) wires `value` + `onChange` through
// this hook; `markSent` is called from the submit path to drop the
// draft once the server has accepted the message.
//
// The 100k-char cap is enforced inside `saveDraft`; this hook surfaces
// the truncation result via a one-shot toast so the user knows their
// long paste was clipped. The toast fires only when the `truncated`
// flag flips — repeated saves of an already-clipped body stay quiet.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  cancelPendingDraftSave,
  draftKey,
  flushDraft,
  loadDraft,
  saveDraftDebounced,
  deleteDraft,
  notifyDraftKeysChanged,
  MAX_DRAFT_LENGTH,
} from "~/lib/drafts";
import { useToast } from "~/context/ToastContext";
import { useMatrix } from "~/context/MatrixContext";

export interface UseDraftResult {
  /** Hydrated draft body. `null` while the initial IDB load is in flight. */
  value: string | null;
  /** Update the draft body. Triggers a debounced save. */
  setValue: (next: string) => void;
  /** Drop the draft (e.g. after a successful send). Cancels pending writes. */
  markSent: () => void;
}

/**
 * Bind a composer to a per-room (or per-thread) draft.
 *
 * `roomId === null` disables the hook — the composer renders without
 * persistence, useful for Storybook fixtures and contexts where no
 * room is yet active. `threadRootId` distinguishes the threaded
 * composer from the main one inside the same room.
 */
export function useDraft(roomId: string | null, threadRootId?: string | null): UseDraftResult {
  const { push } = useToast();
  const { userId } = useMatrix();
  const key = roomId && userId ? draftKey(userId, roomId, threadRootId ?? null) : null;
  const [value, setValueState] = useState<string | null>(null);
  const truncationFiredRef = useRef(false);
  const activeRef = useRef(false);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    let cancelled = false;
    truncationFiredRef.current = false;
    activeRef.current = false;
    if (!key) {
      setValueState(userId ? "" : null);
      return;
    }
    setValueState(null);
    void loadDraft(key).then((body) => {
      if (cancelled) return;
      if (activeRef.current) return;
      setValueState(body);
    });
    return () => {
      cancelled = true;
    };
  }, [key, userId]);

  // Flush any in-flight debounced save when the key changes or the
  // hook unmounts. The flush runs against the previous key so
  // pending writes don't bleed into the next room's store.
  useEffect(() => {
    const previousKey = key;
    return () => {
      if (!previousKey) return;
      if (userIdRef.current === null) {
        cancelPendingDraftSave(previousKey);
        return;
      }
      void flushDraft(previousKey);
    };
  }, [key]);

  const setValue = useCallback(
    (next: string) => {
      activeRef.current = true;
      setValueState(next);
      if (!userId) return;
      if (!key) return;
      // Surface the truncation toast exactly once per draft. Subsequent
      // edits stay quiet until the body falls back below the cap and
      // a fresh truncation occurs.
      if (next.length > MAX_DRAFT_LENGTH) {
        if (!truncationFiredRef.current) {
          truncationFiredRef.current = true;
          push({
            tone: "danger",
            message: `Draft trimmed to ${MAX_DRAFT_LENGTH.toLocaleString()} characters.`,
          });
        }
      } else {
        truncationFiredRef.current = false;
      }
      saveDraftDebounced(key, next);
    },
    [key, push, userId],
  );

  const markSent = useCallback(() => {
    setValueState("");
    if (!key) return;
    // Cancel any optimistic-clear debounce queued by `setValue("")`
    // so we don't double-touch IDB. `deleteDraft` is the authoritative
    // write — no point letting the debounce flush land afterwards.
    cancelPendingDraftSave(key);
    void deleteDraft(key).then(() => notifyDraftKeysChanged());
  }, [key]);

  return { value, setValue, markSent };
}
